import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { QueryState, StatusPill, formatDate } from '@/components/live';
import { callRpc } from '@/api/resources';
import { consentTypeLabel } from '@/lib/consentTypes';
import { isSupabaseConfigured } from '@/lib/supabase';

export interface ConsentPositionRow {
  consent_type: string;
  is_required: boolean;
  is_mandatory: boolean;
  requires_kyc_first: boolean;
  action_keys: string[] | null;
  state: 'missing' | 'pending' | 'valid' | 'expiring' | 'expired' | 'revoked' | string;
  consent_id: string | null;
  status: string | null;
  language_code: string | null;
  valid_from: string | null;
  valid_to: string | null;
  days_left: number | null;
  acknowledged_at: string | null;
  acknowledgement_method: string | null;
}

/** What each state means in one line, because the word alone does not say what to do about it. */
const MEANING: Record<string, string> = {
  missing: 'Required by this party\'s role and never issued.',
  pending: 'Issued, not signed yet.',
  valid: 'Signed and in date.',
  recorded: 'Confirmed by the party; it stays on record.',
  expiring: 'Signed, but lapsing soon. Renew it.',
  expired: 'Out of date. It no longer covers anything.',
  revoked: 'Withdrawn by the party.',
};

/**
 * Where one party stands on every consent (owner, 2026-09-25: "also need to know which party what
 * consent is pending, issued and valid, expired? this shall be at respective place").
 *
 * The consent register lists the consents that EXIST. The state that matters most has no row to
 * list: a consent the party's role requires and nobody has ever issued. A farmer with no
 * data-processing consent looked exactly like a farmer nobody had got round to, because both are
 * an absence.
 *
 * So this is built from the requirements rather than from the records, and the missing ones come
 * first. It is deliberately a small component with one prop, because "at respective place" means
 * it belongs wherever a party is opened rather than on a screen of its own.
 */
export function ConsentPosition({ partyId, onIssue }: { partyId: string; onIssue?: (consentType: string) => void }) {
  const query = useQuery({
    queryKey: ['consent-position', partyId],
    queryFn: () => callRpc<ConsentPositionRow[]>('fn_party_consent_position', { p_party_id: partyId }),
    enabled: isSupabaseConfigured && Boolean(partyId),
  });

  const rows = query.data ?? [];
  const wrong = rows.filter((row) => row.is_mandatory && ['missing', 'expired', 'revoked'].includes(row.state));

  return (
    <section className="consent-position">
      <div className="section-heading">
        <h3>Consent position <span className="muted">({rows.length})</span></h3>
      </div>

      {wrong.length > 0 && (
        <p className="state-note is-warn">
          <ShieldAlert size={14} />
          <span>
            {wrong.length === 1 ? 'One consent this party must have is' : `${wrong.length} consents this party must have are`}
            {' '}not in place: {wrong.map((row) => consentTypeLabel(row.consent_type)).join(', ')}. Actions that
            depend on {wrong.length === 1 ? 'it' : 'them'} will be refused.
          </span>
        </p>
      )}

      <QueryState empty="No consent is required of this party, and none is on file." query={query}>
        <ul className="consent-position-list">
          {rows.map((row) => {
            // A withdrawal is recorded, not "valid until 2036" (found in the 2026-09-28 UI pass).
            const state = row.consent_type === 'withdrawal' && row.state === 'valid' ? 'recorded' : row.state;
            return (
            <li className={`is-${row.state}`} key={row.consent_type}>
              <div>
                <strong>{consentTypeLabel(row.consent_type)}</strong>
                <small className="muted">
                  {row.is_required
                    ? `Required${row.is_mandatory ? '' : ' (optional)'}${row.action_keys?.length ? ` for ${row.action_keys.join(', ').replace(/_/g, ' ')}` : ''}`
                    : 'Not required by their role'}
                  {row.requires_kyc_first ? ' · KYC must be verified first' : ''}
                </small>
              </div>
              <div className="consent-position-state">
                <StatusPill value={state} />
                <small className="muted">
                  {MEANING[state] ?? ''}
                  {row.valid_to && ['valid', 'expiring'].includes(state)
                    ? ` Until ${formatDate(row.valid_to)}${row.days_left != null ? ` (${row.days_left} days)` : ''}.`
                    : row.valid_to && row.state === 'expired' ? ` Lapsed ${formatDate(row.valid_to)}.` : ''}
                </small>
              </div>
              <div className="consent-position-act">
                {/* Issuing one is the officer's job on the consent screen; this says which, and
                    takes them there already searched for this party rather than making them
                    find the row again. */}
                {['missing', 'expired', 'revoked', 'expiring'].includes(row.state) && onIssue && (
                  <button onClick={() => onIssue(row.consent_type)} type="button">
                    {row.state === 'expiring' || row.state === 'expired' ? 'Renew' : 'Issue'}
                  </button>
                )}
                {row.consent_id && (
                  <Link to={`/consent-capture?q=${encodeURIComponent(row.consent_type)}`}>Open</Link>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      </QueryState>
    </section>
  );
}
